import styles from './googleMap.module.css'
import { InfoWindow, Marker, useMap } from "@vis.gl/react-google-maps"
import { Fragment, useEffect, useState } from "react"
import { Button, Icon } from "../../../ui"
import { firstItem, lastItem } from '../../../../utils/listUtils'
import { capitalize } from '../../../../utils/typeUtils'
import { AddressData, GoogleAddress, RouteDuration } from './types'
import { MapCoordinates } from '../../types'

type Props = {
    addressesData: AddressData[]
    onChangeRoute?: (sortedAddressesData: AddressData[], duration: RouteDuration) => void
}

export const GoogleMapContent = (props: Props) => {
    const { addressesData, onChangeRoute } = props
    const showRoute = addressesData.length > 1

    const [addressDataList, setAddressDataList] = useState<GoogleAddress[]>([])
    const [directionsRenderer, setDirectionsRenderer] = useState<google.maps.DirectionsRenderer | null>(null)
    const [optimizedWaypointOrder, setOptimizedWaypointOrder] = useState<number[]>([])
    const [currentZoom, setCurrentZoom] = useState(12)
    const [visibleInfoWindows, setVisibleInfoWindows] = useState<boolean[]>(new Array(addressesData.length).fill(true))

    const map = useMap()

    useEffect(() => {
        if (!map) return

        const zoomListener = map.addListener('zoom_changed', () => {
            setCurrentZoom(map.getZoom() || 12)
        })

        const renderer = new google.maps.DirectionsRenderer({
            map,
            suppressMarkers: true,
        })
        setDirectionsRenderer(renderer)

        return () => {
            zoomListener.remove()
            renderer.setMap(null)
        }
    }, [map])

    useEffect(() => {
        if (!map) return

        setVisibleInfoWindows(new Array(addressesData.length).fill(true))

        const geocoder = new google.maps.Geocoder()
        Promise.all(addressesData.map(({ address }) =>
            new Promise<GoogleAddress>((resolve, reject) => {
                geocoder.geocode({ address }, (results, status) => {
                    if (status === 'OK' && results && firstItem(results)) {
                        const result = firstItem(results)!
                        const location = result.geometry.location
                        resolve({
                            coordinates: {
                                lat: location.lat(),
                                lng: location.lng(),
                            },
                            description: result.formatted_address
                        })
                    } else {
                        reject(new Error(`Geocoding failed for ${address}`))
                    }
                })
            })
        ))
        .then(setAddressDataList)
        .then(() => {
            if (addressDataList.length === 1) {
                map.setCenter(firstItem(addressDataList)!.coordinates)
            }
        })
    }, [addressesData.map(({ address }) => address).join(';')])

    useEffect(() => {
        if (!directionsRenderer || !showRoute) return
    
        const service = new google.maps.DirectionsService()
        const waypoints = addressesData.slice(1, -1).map(({ address }) => ({
            location: address,
            stopover: true
        }))
    
        service.route(
            {
                origin: firstItem(addressesData)!.address,
                destination: lastItem(addressesData)!.address,
                waypoints,
                optimizeWaypoints: true,
                travelMode: google.maps.TravelMode.DRIVING,
            },
            (result, status) => {
                if (status === 'OK' && result) {
                    directionsRenderer.setDirections(result)
                    const route = firstItem(result.routes)
                    if (route?.waypoint_order) {
                        const optimizedOrder = route.waypoint_order.map(index => index + 1)
                        setOptimizedWaypointOrder(optimizedOrder)
                        
                        if (onChangeRoute) {
                            const fromAddressData = firstItem(addressesData)!
                            const orderedAddressesData: AddressData[] = [
                                {
                                    key: fromAddressData.key,
                                    address: fromAddressData.address
                                }
                            ]
                            optimizedOrder.forEach(index => {
                                orderedAddressesData.push(addressesData[index])
                            })
                            const toAddressData = lastItem(addressesData)!
                            orderedAddressesData.push({
                                key: toAddressData.key,
                                address: toAddressData.address
                            })
                            const duration = route.legs.reduce((total, leg) => {
                                if (!leg.duration?.value) return total
                                return total + leg.duration.value
                            }, 0)
                            const hours = Math.floor(duration / 3600)
                            const minutes = Math.floor((duration % 3600) / 60)
                            onChangeRoute(orderedAddressesData, { hours, minutes })
                        }
                    }
                }
            }
        )
    }, [directionsRenderer, addressesData.length])

    const shiftedUp = (coordinates: MapCoordinates) => {
        const zoomDiff = currentZoom - 12
        const offset = 0.01 * Math.pow(2, -zoomDiff)
        return {
            lat: coordinates.lat + offset,
            lng: coordinates.lng
        }
    }

    const onClickMarker = (index: number) => () => {
        const newVisibleInfoWindows = [...visibleInfoWindows]
        newVisibleInfoWindows[index] = !newVisibleInfoWindows[index]
        setVisibleInfoWindows(newVisibleInfoWindows)
    }

    const getOptimizedStopNumber = (index: number) => {
        if (index === 0) return 0
        if (index === addressesData.length - 1) return addressesData.length - 1
        const optimizedIndex = optimizedWaypointOrder.indexOf(index)
        if (optimizedIndex === -1) return index
        return optimizedIndex + 1
    }

    const generateGoogleMapsUrl = () => {
        if (addressesData.length === 0) {
            return ''
        } else if (addressesData.length === 1) {
            return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(firstItem(addressesData)!.address)}`
        }
        const origin = encodeURIComponent(firstItem(addressesData)!.address)
        const destination = encodeURIComponent(lastItem(addressesData)!.address)
        if (addressesData.length === 2) {
            return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`
        }
        const waypoints = addressesData.slice(1, -1).map(({ address }) => encodeURIComponent(address)).join('|')
        return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&waypoints=${waypoints}&travelmode=driving`
    }

    const handleOpenInGoogleMaps = () => {
        const url = generateGoogleMapsUrl()
        if (url) {
            window.open(url, '_blank')
        }
    }

    return (
        <>
            {addressesData.length > 0 && (
                <div className={styles.googleMapsLinkContainer}>
                    <Button
                        label="Google Maps"
                        icon="arrow-up-right-square"
                        onClick={handleOpenInGoogleMaps}
                    />
                </div>
            )}
            {addressDataList.map((addressData, index) => {
                const { coordinates, description } = addressData
                const optimizedStopNumber = getOptimizedStopNumber(index)
                const infoSubtitle = index === 0 ? 'Salida' : index === addressesData.length - 1 ? 'Llegada' : `Parada ${optimizedStopNumber}`
                return (
                    <Fragment key={index}>
                        <Marker
                            position={coordinates}
                            onClick={onClickMarker(index)}
                        />
                        {visibleInfoWindows[index] && (
                            <InfoWindow
                                position={shiftedUp(coordinates)}
                                onCloseClick={onClickMarker(index)}
                            >
                                <div className={styles.infoContainer}>
                                    <div className={styles.infoTextContainer}>
                                        {showRoute && (
                                            <div className={styles.infoPrefix}>{infoSubtitle}</div>
                                        )}
                                        <div className={styles.infoAddress}>{capitalize(description)}</div>
                                    </div>
                                    <b>
                                    <Icon
                                        icon="x"
                                        color="tertiary"
                                        onClick={onClickMarker(index)}
                                    />
                                    </b>
                                </div>
                            </InfoWindow>
                        )}
                    </Fragment>
                )
            })}
        </>
    )
}
